import { formatUnits, type ProtocolPool } from "@zkresistor/shared";

const COMPACT_UNITS = [
  { threshold: 1_000_000_000_000n, suffix: "T" },
  { threshold: 1_000_000_000n, suffix: "B" },
  { threshold: 1_000_000n, suffix: "M" },
  { threshold: 1_000n, suffix: "K" },
] as const;

export function activeDeposits(pool: ProtocolPool): number {
  return pool.nextIndex - pool.withdrawalCount;
}

export function formatShieldedTvl(pool: ProtocolPool): string {
  return formatCompactTokenAmount(BigInt(activeDeposits(pool)) * pool.denomination, pool.decimals);
}

export type PoolSort = "tvl" | "privacy";

export function poolTvlNanoGram(pool: ProtocolPool): bigint | null {
  if (pool.kind === "ton") return BigInt(activeDeposits(pool)) * pool.denomination;
  return pool.estimatedTvlNanoGram;
}

export function isLowLiquidityJetton(pool: ProtocolPool): boolean {
  return pool.kind === "jetton" && pool.estimatedTvlNanoGram === null;
}

export function sortPools(pools: readonly ProtocolPool[], sort: PoolSort): ProtocolPool[] {
  return [...pools].sort((left, right) => {
    if (sort === "privacy" && left.privacySet !== right.privacySet) {
      return right.privacySet - left.privacySet;
    }
    if (sort === "tvl") {
      const leftTvl = poolTvlNanoGram(left);
      const rightTvl = poolTvlNanoGram(right);
      if (leftTvl === null && rightTvl !== null) return 1;
      if (leftTvl !== null && rightTvl === null) return -1;
      if (leftTvl !== null && rightTvl !== null && leftTvl !== rightTvl) {
        return leftTvl > rightTvl ? -1 : 1;
      }
    }
    if (left.privacySet !== right.privacySet) return right.privacySet - left.privacySet;
    return left.address.localeCompare(right.address);
  });
}

export function formatPoolTvlGram(pool: ProtocolPool): string | null {
  const value = poolTvlNanoGram(pool);
  return value === null ? null : formatCompactTokenAmount(value, 9);
}

export function formatCompactTokenAmount(value: bigint, decimals: number): string {
  const negative = value < 0n;
  const absolute = negative ? -value : value;
  const exact = `${negative ? "-" : ""}${formatUnits(absolute, decimals)}`;
  const base = 10n ** BigInt(decimals);
  let unitIndex = COMPACT_UNITS.findIndex(({ threshold }) => absolute >= base * threshold);
  if (unitIndex === -1) return exact;

  let unit = COMPACT_UNITS[unitIndex]!;
  let divisor = base * unit.threshold;
  let hundredths = (absolute * 100n + divisor / 2n) / divisor;
  if (hundredths >= 100_000n && unitIndex > 0) {
    unit = COMPACT_UNITS[--unitIndex]!;
    divisor = base * unit.threshold;
    hundredths = (absolute * 100n + divisor / 2n) / divisor;
  }

  const whole = hundredths / 100n;
  const fraction = (hundredths % 100n).toString().padStart(2, "0").replace(/0+$/, "");
  return `${negative ? "-" : ""}${whole}${fraction === "" ? "" : `.${fraction}`}${unit.suffix}`;
}

export function shortAddress(value: string): string {
  return `${value.slice(0, 6)}...${value.slice(-5)}`;
}
